/**
 * PCOS Scoring Algorithm - NovaMed Implementation
 * 
 * Classifies patients into phenotypes (A-D) and endotypes based on:
 * - Rotterdam criteria (hyperandrogenism, ovulatory dysfunction, polycystic ovaries)
 * - Metabolic markers (insulin resistance, inflammation)
 * - Lab values when available
 */

import {
  PCOSQuizData,
  PCOSAssessmentResult,
  PCOSPhenotype,
  PCOSEndotype,
  PCOSScores,
  DomainScore,
} from './types';

// ============================================================================
// SCORING CONSTANTS
// ============================================================================

const WEIGHTS = {
  hyperandrogenism: {
    hirsutism: { mild: 1, moderate: 2, severe: 3 },
    acne: { mild: 0.5, moderate: 1, severe: 2 },
    hairThinning: 1.5,
    elevatedTestosterone: 3,
    elevatedDHEAS: 2,
  },
  ovulatoryDysfunction: {
    irregularCycles: 2,
    absentCycles: 3,
    noOvulation: 2.5,
    infertility: 1.5,
  },
  polycysticOvaries: {
    ultrasoundPositive: 3,
    elevatedAMH: 2.5,
    multiFollicular: 2,
  },
  insulinResistance: {
    centralObesity: 2,
    highFastingInsulin: 3,
    homaIrElevated: 3.5,
    prediabetes: 2.5,
    diabetes: 3,
    weightDifficulty: 1.5,
    familyDiabetes: 1,
    metabolicMeds: 2,
  },
  inflammation: {
    autoimmune: 2.5,
    gutSymptoms: 2,
    chronicFatigue: 1.5,
    jointPain: 1,
    elevatedCRP: 3,
    foodSensitivities: 1.5,
  },
  adrenal: {
    elevatedDHEAS: 3,
    normalTestosterone: 2,
    highStress: 2,
    poorSleep: 1.5,
    anxiety: 1,
  },
  postPill: {
    recentlyStopped: 3,
    symptomsAfterStopping: 2.5,
    previouslyRegular: 2,
    lowInsulinResistance: 1.5,
  },
};

const THRESHOLDS = {
  phenotype: {
    hyperandrogenism: 3,
    ovulatoryDysfunction: 2.5,
    polycysticOvaries: 2.5,
  },
  lab: {
    testosterone: { high: 70 },
    dheaS: { high: 350 },
    amh: { elevated: 4.5 },
    homaIr: { elevated: 2.5 },
    lhFshRatio: { elevated: 2 },
    crp: { elevated: 3 },
    fastingInsulin: { elevated: 15 },
  },
};

// ============================================================================
// SCORING FUNCTIONS
// ============================================================================

function calculateHyperandrogenismScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.hyperandrogenic.hirsutism.present) {
    const severityScore = WEIGHTS.hyperandrogenism.hirsutism[data.hyperandrogenic.hirsutism.severity as keyof typeof WEIGHTS.hyperandrogenism.hirsutism] || 0;
    score += severityScore;
    if (severityScore > 0) factors.push(`Hirsutism (${data.hyperandrogenic.hirsutism.severity})`);
  }
  
  if (data.hyperandrogenic.acne.present) {
    const severityScore = WEIGHTS.hyperandrogenism.acne[data.hyperandrogenic.acne.severity as keyof typeof WEIGHTS.hyperandrogenism.acne] || 0;
    score += severityScore;
    if (data.hyperandrogenic.acne.persistent) {
      score += 0.5;
      factors.push('Persistent adult acne');
    } else if (severityScore > 0) {
      factors.push(`Acne (${data.hyperandrogenic.acne.severity})`);
    }
  }
  
  if (data.hyperandrogenic.hairThinning.present) {
    score += WEIGHTS.hyperandrogenism.hairThinning;
    factors.push('Androgenic hair thinning');
  }
  
  if (data.hyperandrogenic.labResults.hasElevatedAndrogens === true) {
    score += WEIGHTS.hyperandrogenism.elevatedTestosterone;
    factors.push('Elevated androgens on lab testing');
  }
  
  if (data.labData) {
    if (data.labData.totalTestosterone?.value && data.labData.totalTestosterone.value > THRESHOLDS.lab.testosterone.high) {
      score += WEIGHTS.hyperandrogenism.elevatedTestosterone;
      factors.push(`Elevated testosterone: ${data.labData.totalTestosterone.value} ${data.labData.totalTestosterone.unit}`);
    }
    if (data.labData.dhea?.value && data.labData.dhea.value > THRESHOLDS.lab.dheaS.high) {
      score += WEIGHTS.hyperandrogenism.elevatedDHEAS;
      factors.push(`Elevated DHEA-S: ${data.labData.dhea.value} ${data.labData.dhea.unit}`);
    }
  }
  
  if (data.hyperandrogenic.voiceChanges || data.hyperandrogenic.clitoromegaly) {
    score += 2;
    factors.push('Severe virilization signs present');
  }
  
  const maxScore = 12;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateOvulatoryDysfunctionScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.menstrual.cycleRegularity === 'irregular') {
    score += WEIGHTS.ovulatoryDysfunction.irregularCycles;
    factors.push('Irregular menstrual cycles');
  } else if (data.menstrual.cycleRegularity === 'absent') {
    score += WEIGHTS.ovulatoryDysfunction.absentCycles;
    factors.push('Absent menstrual cycles');
  } else if (data.menstrual.cycleRegularity === 'variable') {
    score += WEIGHTS.ovulatoryDysfunction.irregularCycles * 0.75;
    factors.push('Highly variable cycle length');
  }
  
  if (data.menstrual.averageCycleLength < 24) {
    score += 1;
    factors.push(`Short cycles (${data.menstrual.averageCycleLength} days)`);
  } else if (data.menstrual.averageCycleLength > 35) {
    score += 1.5;
    factors.push(`Long cycles (${data.menstrual.averageCycleLength} days)`);
  }
  
  if (data.menstrual.tracksOvulation && data.menstrual.detectsOvulationRegularly === false) {
    score += WEIGHTS.ovulatoryDysfunction.noOvulation;
    factors.push('Does not detect regular ovulation');
  }
  
  if (data.menstrual.pregnancyHistory.troubleConceiving) {
    score += WEIGHTS.ovulatoryDysfunction.infertility;
    factors.push('Difficulty conceiving');
  }
  if (data.menstrual.pregnancyHistory.miscarriages > 0) {
    score += 0.5 * Math.min(data.menstrual.pregnancyHistory.miscarriages, 3);
    factors.push(`History of miscarriage(s)`);
  }
  
  const maxScore = 10;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculatePolycysticOvariesScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.menstrual.hasHadUltrasound) {
    if (data.menstrual.ultrasoundResult === 'polycystic') {
      score += WEIGHTS.polycysticOvaries.ultrasoundPositive;
      factors.push('Polycystic ovaries on ultrasound');
    } else if (data.menstrual.ultrasoundResult === 'multi-follicular') {
      score += WEIGHTS.polycysticOvaries.multiFollicular;
      factors.push('Multi-follicular ovaries on ultrasound');
    }
  }
  
  if (data.menstrual.amhTested && data.menstrual.amhValue) {
    let amhNgMl = data.menstrual.amhValue;
    if (data.menstrual.amhUnit === 'pmol/L') {
      amhNgMl = data.menstrual.amhValue / 7.14;
    }
    
    if (amhNgMl > THRESHOLDS.lab.amh.elevated) {
      score += WEIGHTS.polycysticOvaries.elevatedAMH;
      factors.push(`Elevated AMH: ${data.menstrual.amhValue} ${data.menstrual.amhUnit}`);
    }
  }
  
  if (data.labData?.amh?.value && data.labData.amh.value > THRESHOLDS.lab.amh.elevated) {
    if (!factors.some(f => f.includes('AMH'))) {
      score += WEIGHTS.polycysticOvaries.elevatedAMH;
      factors.push(`Elevated AMH: ${data.labData.amh.value} ${data.labData.amh.unit}`);
    }
  }
  
  if (data.labData?.lhFshRatio && data.labData.lhFshRatio > THRESHOLDS.lab.lhFshRatio.elevated) {
    score += 1.5;
    factors.push(`Elevated LH:FSH ratio: ${data.labData.lhFshRatio.toFixed(1)}`);
  }
  
  const maxScore = 7;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateInsulinResistanceScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.metabolic.weightDifficulty === 'moderate') {
    score += WEIGHTS.insulinResistance.weightDifficulty;
    factors.push('Moderate difficulty managing weight');
  } else if (data.metabolic.weightDifficulty === 'severe') {
    score += WEIGHTS.insulinResistance.weightDifficulty * 1.5;
    factors.push('Significant difficulty managing weight');
  }
  
  if (data.metabolic.centralObesity) {
    score += WEIGHTS.insulinResistance.centralObesity;
    factors.push('Central/abdominal obesity');
  }
  
  if (data.metabolic.waistHipRatio && data.metabolic.waistHipRatio > 0.85) {
    score += 1;
    factors.push('Elevated waist-to-hip ratio');
  }
  
  if (data.metabolic.diabetesHistory.prediabetes) {
    score += WEIGHTS.insulinResistance.prediabetes;
    factors.push('History of pre-diabetes');
  }
  if (data.metabolic.diabetesHistory.type2Diabetes) {
    score += WEIGHTS.insulinResistance.diabetes;
    factors.push('Type 2 diabetes diagnosis');
  }
  if (data.metabolic.diabetesHistory.gestationalDiabetes) {
    score += WEIGHTS.insulinResistance.prediabetes;
    factors.push('History of gestational diabetes');
  }
  if (data.metabolic.diabetesHistory.insulinResistanceDiagnosis) {
    score += WEIGHTS.insulinResistance.homaIrElevated;
    factors.push('Diagnosed insulin resistance');
  }
  
  if (data.metabolic.diabetesMedications.length > 0) {
    score += WEIGHTS.insulinResistance.metabolicMeds;
    factors.push(`On metabolic medications: ${data.metabolic.diabetesMedications.join(', ')}`);
  }
  
  if (data.familyHistory.diabetesInFamily) {
    score += WEIGHTS.insulinResistance.familyDiabetes;
    factors.push('Family history of diabetes');
  }
  
  if (data.metabolic.lipidAbnormalities) {
    score += 1;
    factors.push('Lipid abnormalities');
  }
  
  if (data.labData) {
    if (data.labData.homaIr && data.labData.homaIr > THRESHOLDS.lab.homaIr.elevated) {
      score += WEIGHTS.insulinResistance.homaIrElevated;
      factors.push(`Elevated HOMA-IR: ${data.labData.homaIr.toFixed(2)}`);
    }
    if (data.labData.fastingInsulin?.value && data.labData.fastingInsulin.value > THRESHOLDS.lab.fastingInsulin.elevated) {
      score += WEIGHTS.insulinResistance.highFastingInsulin;
      factors.push(`Elevated fasting insulin: ${data.labData.fastingInsulin.value} ${data.labData.fastingInsulin.unit}`);
    }
  }
  
  const maxScore = 18;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateInflammationScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.inflammatory.autoimmuneDiagnoses.length > 0) {
    score += WEIGHTS.inflammation.autoimmune;
    factors.push(`Autoimmune conditions: ${data.inflammatory.autoimmuneDiagnoses.join(', ')}`);
  }
  
  const gutIssues = [
    data.inflammatory.gutSymptoms.ibs,
    data.inflammatory.gutSymptoms.bloating,
    data.inflammatory.gutSymptoms.constipation,
    data.inflammatory.gutSymptoms.diarrhea,
  ].filter(Boolean).length;
  
  if (gutIssues >= 2) {
    score += WEIGHTS.inflammation.gutSymptoms;
    factors.push('Multiple gut symptoms present');
  } else if (gutIssues === 1) {
    score += WEIGHTS.inflammation.gutSymptoms * 0.5;
    factors.push('Some gut symptoms present');
  }
  
  if (data.inflammatory.gutSymptoms.foodSensitivities.length > 0) {
    score += WEIGHTS.inflammation.foodSensitivities;
    factors.push('Food sensitivities reported');
  }
  
  if (data.inflammatory.chronicFatigue) {
    score += WEIGHTS.inflammation.chronicFatigue;
    factors.push('Chronic fatigue');
  }
  
  if (data.inflammatory.jointPain) {
    score += WEIGHTS.inflammation.jointPain;
    factors.push('Joint pain');
  }
  
  if (data.inflammatory.frequentHeadaches) {
    score += 0.5;
    factors.push('Frequent headaches');
  }
  
  if (data.labData?.crp?.value && data.labData.crp.value > THRESHOLDS.lab.crp.elevated) {
    score += WEIGHTS.inflammation.elevatedCRP;
    factors.push(`Elevated CRP: ${data.labData.crp.value} ${data.labData.crp.unit}`);
  } else if (data.inflammatory.crpTested && data.inflammatory.crpValue && data.inflammatory.crpValue > THRESHOLDS.lab.crp.elevated) {
    score += WEIGHTS.inflammation.elevatedCRP;
    factors.push(`Elevated CRP: ${data.inflammatory.crpValue} mg/L`);
  }
  
  const maxScore = 12;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateAdrenalScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  const hasDHEASElevation = (
    (data.labData?.dhea?.value && data.labData.dhea.value > THRESHOLDS.lab.dheaS.high) ||
    (data.hyperandrogenic.labResults.dheaS && data.hyperandrogenic.labResults.dheaS > THRESHOLDS.lab.dheaS.high)
  );
  
  const hasNormalTestosterone = (
    !data.labData?.totalTestosterone?.value ||
    data.labData.totalTestosterone.value <= THRESHOLDS.lab.testosterone.high
  );
  
  if (hasDHEASElevation) {
    score += WEIGHTS.adrenal.elevatedDHEAS;
    factors.push('Elevated DHEA-S');
    
    if (hasNormalTestosterone) {
      score += WEIGHTS.adrenal.normalTestosterone;
      factors.push('Normal testosterone (adrenal pattern)');
    }
  }
  
  if (data.mentalHealth.stressLevel === 'high' || data.mentalHealth.stressLevel === 'very-high') {
    score += WEIGHTS.adrenal.highStress;
    factors.push(`High stress level: ${data.mentalHealth.stressLevel}`);
  }
  
  if (data.mentalHealth.sleepQuality === 'poor' || data.mentalHealth.sleepQuality === 'very-poor') {
    score += WEIGHTS.adrenal.poorSleep;
    factors.push('Poor sleep quality');
  }
  
  if (data.mentalHealth.anxietySymptoms === 'moderate' || data.mentalHealth.anxietySymptoms === 'severe') {
    score += WEIGHTS.adrenal.anxiety;
    factors.push('Significant anxiety symptoms');
  }
  
  if (data.labData?.cortisol?.value && data.labData.cortisol.value > 23) {
    score += 1.5;
    factors.push(`Elevated cortisol: ${data.labData.cortisol.value} ${data.labData.cortisol.unit}`);
  }
  
  const maxScore = 12;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculatePostPillScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.demographics.recentlyStoppedContraceptive) {
    score += WEIGHTS.postPill.recentlyStopped;
    factors.push('Recently stopped hormonal contraceptives');
    
    if (data.demographics.monthsSinceStoppedContraceptive && data.demographics.monthsSinceStoppedContraceptive < 12) {
      score += 1;
      factors.push(`Only ${data.demographics.monthsSinceStoppedContraceptive} months since stopping`);
    }
  }
  
  if (data.demographics.recentlyStoppedContraceptive && data.menstrual.cycleRegularity !== 'regular') {
    score += WEIGHTS.postPill.symptomsAfterStopping;
    factors.push('Irregular cycles developed after stopping pill');
  }
  
  if (!data.metabolic.diabetesHistory.insulinResistanceDiagnosis && 
      data.metabolic.weightDifficulty === 'none' &&
      !data.metabolic.centralObesity) {
    score += WEIGHTS.postPill.lowInsulinResistance;
    factors.push('No signs of insulin resistance');
  }
  
  const maxScore = 10;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateMentalHealthRiskScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  const anxietyScores = { none: 0, mild: 1, moderate: 2, severe: 3 };
  const depressionScores = { none: 0, mild: 1, moderate: 2, severe: 3 };
  
  if (data.mentalHealth.anxietySymptoms !== 'none') {
    score += anxietyScores[data.mentalHealth.anxietySymptoms];
    factors.push(`${data.mentalHealth.anxietySymptoms.charAt(0).toUpperCase() + data.mentalHealth.anxietySymptoms.slice(1)} anxiety symptoms`);
  }
  
  if (data.mentalHealth.depressionSymptoms !== 'none') {
    score += depressionScores[data.mentalHealth.depressionSymptoms];
    factors.push(`${data.mentalHealth.depressionSymptoms.charAt(0).toUpperCase() + data.mentalHealth.depressionSymptoms.slice(1)} depression symptoms`);
  }
  
  if (data.mentalHealth.sleepQuality === 'poor') {
    score += 1;
    factors.push('Poor sleep quality');
  } else if (data.mentalHealth.sleepQuality === 'very-poor') {
    score += 2;
    factors.push('Very poor sleep quality');
  }
  
  if (data.mentalHealth.fatigueSeverity === 'moderate') {
    score += 1;
    factors.push('Moderate fatigue');
  } else if (data.mentalHealth.fatigueSeverity === 'severe') {
    score += 2;
    factors.push('Severe fatigue');
  }
  
  if (data.demographics.eatingDisorderHistory) {
    score += 2;
    factors.push('History of eating disorder');
  }
  
  const maxScore = 12;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateMetabolicRiskScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  const heightM = data.demographics.heightCm / 100;
  const bmi = data.demographics.weightKg / (heightM * heightM);
  
  if (bmi >= 25 && bmi < 30) {
    score += 1;
    factors.push(`Overweight (BMI ${bmi.toFixed(1)})`);
  } else if (bmi >= 30 && bmi < 35) {
    score += 2;
    factors.push(`Obese class I (BMI ${bmi.toFixed(1)})`);
  } else if (bmi >= 35) {
    score += 3;
    factors.push(`Obese class II+ (BMI ${bmi.toFixed(1)})`);
  }
  
  if (data.metabolic.centralObesity) {
    score += 2;
    factors.push('Central obesity');
  }
  
  if (data.metabolic.highBloodPressure) {
    score += 2;
    factors.push('High blood pressure');
  }
  
  if (data.metabolic.lipidAbnormalities) {
    score += 1.5;
    factors.push('Lipid abnormalities');
  }
  
  if (data.metabolic.diabetesHistory.type2Diabetes) {
    score += 3;
    factors.push('Type 2 diabetes');
  } else if (data.metabolic.diabetesHistory.prediabetes) {
    score += 2;
    factors.push('Pre-diabetes');
  }
  
  if (data.familyHistory.cardiovascularDiseaseInFamily) {
    score += 1;
    factors.push('Family history of cardiovascular disease');
  }
  
  const maxScore = 14;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

function calculateGLP1SuitabilityScore(data: PCOSQuizData): DomainScore {
  let score = 0;
  const factors: string[] = [];
  
  if (data.metabolic.diabetesHistory.insulinResistanceDiagnosis || data.metabolic.diabetesHistory.prediabetes) {
    score += 3;
    factors.push('Insulin resistance present - GLP-1s can help');
  }
  
  if (data.metabolic.weightDifficulty === 'moderate' || data.metabolic.weightDifficulty === 'severe') {
    score += 2;
    factors.push('Would benefit from weight management support');
  }
  
  if (data.metabolic.interestedInGlp1) {
    score += 1;
    factors.push('Interested in pharmacological options');
  }
  
  if (data.metabolic.diabetesMedications.some(m => m.toLowerCase().includes('metformin'))) {
    score += 1;
    factors.push('Prior metformin use');
  }
  
  const heightM = data.demographics.heightCm / 100;
  const bmi = data.demographics.weightKg / (heightM * heightM);
  if (bmi >= 27) {
    score += 2;
    factors.push(`BMI ${bmi.toFixed(1)} meets threshold for weight management indication`);
  }
  
  const maxScore = 9;
  return { score, maxScore, percentage: Math.min((score / maxScore) * 100, 100), factors };
}

// ============================================================================
// PHENOTYPE & ENDOTYPE CLASSIFICATION
// ============================================================================

function classifyPhenotype(scores: PCOSScores): { phenotype: PCOSPhenotype; confidence: number; explanation: string } {
  const ha = scores.hyperandrogenism.score >= THRESHOLDS.phenotype.hyperandrogenism;
  const od = scores.ovulatoryDysfunction.score >= THRESHOLDS.phenotype.ovulatoryDysfunction;
  const pco = scores.polycysticOvaries.score >= THRESHOLDS.phenotype.polycysticOvaries;
  
  if (ha && od && pco) {
    return {
      phenotype: 'A',
      confidence: Math.min(scores.hyperandrogenism.percentage, scores.ovulatoryDysfunction.percentage, scores.polycysticOvaries.percentage),
      explanation: 'Classic PCOS: All three Rotterdam criteria are present (hyperandrogenism, ovulatory dysfunction, and polycystic ovaries).',
    };
  }
  
  if (ha && od && !pco) {
    return {
      phenotype: 'B',
      confidence: Math.min(scores.hyperandrogenism.percentage, scores.ovulatoryDysfunction.percentage),
      explanation: 'Non-PCO PCOS: Hyperandrogenism and ovulatory dysfunction are present, but ovaries appear normal on imaging.',
    };
  }
  
  if (ha && !od && pco) {
    return {
      phenotype: 'C',
      confidence: Math.min(scores.hyperandrogenism.percentage, scores.polycysticOvaries.percentage),
      explanation: 'Ovulatory PCOS: Hyperandrogenism and polycystic ovaries are present, but cycles may appear regular.',
    };
  }
  
  if (!ha && od && pco) {
    return {
      phenotype: 'D',
      confidence: Math.min(scores.ovulatoryDysfunction.percentage, scores.polycysticOvaries.percentage),
      explanation: 'Non-Hyperandrogenic PCOS: Ovulatory dysfunction and polycystic ovaries without significant androgen excess.',
    };
  }
  
  const highestScore = Math.max(scores.hyperandrogenism.percentage, scores.ovulatoryDysfunction.percentage, scores.polycysticOvaries.percentage);
  
  return {
    phenotype: 'uncertain',
    confidence: highestScore * 0.5,
    explanation: 'Your responses don\'t fully meet Rotterdam criteria for PCOS diagnosis. However, some symptoms are present. We recommend consulting a healthcare provider for proper evaluation.',
  };
}

function classifyEndotype(scores: PCOSScores): { endotype: PCOSEndotype; confidence: number; explanation: string } {
  const endotypeScores = {
    'insulin-resistant': scores.insulinResistance.percentage,
    'inflammatory': scores.inflammation.percentage,
    'adrenal': scores.adrenalFactors.percentage,
    'post-pill': scores.postPillFactors.percentage,
  };
  
  const entries = Object.entries(endotypeScores) as [PCOSEndotype, number][];
  entries.sort((a, b) => b[1] - a[1]);
  
  const [topEndotype, topScore] = entries[0];
  const [secondEndotype, secondScore] = entries[1];
  
  if (topScore - secondScore < 15 && topScore > 40 && secondScore > 40) {
    return {
      endotype: 'mixed',
      confidence: (topScore + secondScore) / 2,
      explanation: `Your profile suggests a mixed presentation with both ${topEndotype} and ${secondEndotype} characteristics. Treatment should address both underlying factors.`,
    };
  }
  
  if (topScore >= 50) {
    const explanations: Record<string, string> = {
      'insulin-resistant': 'Your PCOS appears to be primarily driven by insulin resistance. Focus on blood sugar management through diet, exercise, and potentially medications.',
      'inflammatory': 'Chronic inflammation appears to be a key driver of your PCOS. Address gut health, identify food sensitivities, and reduce inflammatory triggers.',
      'adrenal': 'Your PCOS shows signs of adrenal involvement. Prioritize stress management, quality sleep, and adrenal support strategies.',
      'post-pill': 'Your symptoms appear related to recent discontinuation of hormonal contraceptives. This type often resolves over 6-12 months.',
    };
    
    return {
      endotype: topEndotype,
      confidence: topScore,
      explanation: explanations[topEndotype] || '',
    };
  }
  
  return {
    endotype: 'uncertain',
    confidence: topScore,
    explanation: 'No single driving factor clearly dominates your PCOS presentation. A comprehensive approach addressing multiple factors may be most effective.',
  };
}

// ============================================================================
// MAIN SCORING FUNCTION
// ============================================================================

export function calculatePCOSAssessment(data: PCOSQuizData): PCOSAssessmentResult {
  const scores: PCOSScores = {
    ovulatoryDysfunction: calculateOvulatoryDysfunctionScore(data),
    hyperandrogenism: calculateHyperandrogenismScore(data),
    polycysticOvaries: calculatePolycysticOvariesScore(data),
    insulinResistance: calculateInsulinResistanceScore(data),
    inflammation: calculateInflammationScore(data),
    adrenalFactors: calculateAdrenalScore(data),
    postPillFactors: calculatePostPillScore(data),
    mentalHealthRisk: calculateMentalHealthRiskScore(data),
    metabolicRisk: calculateMetabolicRiskScore(data),
    glp1Suitability: calculateGLP1SuitabilityScore(data),
  };
  
  const { phenotype, confidence: phenotypeConfidence, explanation: phenotypeExplanation } = classifyPhenotype(scores);
  const { endotype, confidence: endotypeConfidence, explanation: endotypeExplanation } = classifyEndotype(scores);
  
  // Generate recommendations
  const recommendations = {
    lifestyle: [
      'Focus on balanced nutrition with adequate protein, fiber, and healthy fats',
      'Aim for 150+ minutes of moderate exercise weekly, including both cardio and strength training',
      'Prioritize 7-9 hours of quality sleep',
    ],
    testing: [] as string[],
    treatments: [] as string[],
    specialists: ['Reproductive endocrinologist or gynecologist with PCOS expertise'],
    resources: ['PCOS Challenge (support organization)', '2023 International Evidence-based Guideline for PCOS'],
  };
  
  if (endotype === 'insulin-resistant' || scores.insulinResistance.percentage > 40) {
    recommendations.lifestyle.push('Reduce refined carbohydrates and added sugars');
    recommendations.treatments.push('Discuss metformin with your healthcare provider', 'Inositol supplementation');
  }
  
  if (endotype === 'inflammatory' || scores.inflammation.percentage > 40) {
    recommendations.lifestyle.push('Consider an elimination diet to identify food triggers');
    recommendations.treatments.push('Omega-3 fatty acid supplementation');
    recommendations.testing.push('Comprehensive stool analysis', 'Food sensitivity testing');
  }
  
  if (!data.labData) {
    recommendations.testing.push('Comprehensive hormone panel', 'Metabolic panel');
  }
  
  // GLP-1 assessment
  const contraindicated = data.glp1Screening.pancreatitisHistory || data.glp1Screening.severeGiCondition || 
    data.glp1Screening.mtcMen2History || data.demographics.pregnancyStatus === 'pregnant' || 
    data.demographics.pregnancyStatus === 'breastfeeding';
  
  const glp1Assessment = {
    suitable: !contraindicated,
    contraindicated,
    reasons: contraindicated ? ['Contraindication present'] : ['No absolute contraindications to GLP-1 therapy'],
    cautions: [] as string[],
    recommendedProtocol: contraindicated ? 'none' as const : (scores.insulinResistance.percentage > 50 ? 'standard' as const : 'microdose' as const),
  };
  
  // Risk flags
  const riskFlags = {
    highInsulinResistance: scores.insulinResistance.percentage > 60,
    significantInflammation: scores.inflammation.percentage > 50,
    mentalHealthConcern: scores.mentalHealthRisk.percentage > 50,
    metabolicSyndromeRisk: scores.metabolicRisk.percentage > 60,
    fertilityConcern: scores.ovulatoryDysfunction.percentage > 60 && 
      (data.demographics.pregnancyStatus === 'trying-to-conceive' || data.menstrual.pregnancyHistory.troubleConceiving),
    possibleTumor: (data.hyperandrogenic.labResults.totalTestosterone && data.hyperandrogenic.labResults.totalTestosterone > 200) ||
      data.hyperandrogenic.voiceChanges || data.hyperandrogenic.clitoromegaly || false,
  };
  
  return {
    likelyPhenotype: phenotype,
    phenotypeConfidence,
    phenotypeExplanation,
    likelyEndotype: endotype,
    endotypeConfidence,
    endotypeExplanation,
    scores,
    riskFlags,
    recommendations,
    glp1Assessment,
    nimCoreRecommendation: {
      recommended: true,
      markersToTrack: ['Total Testosterone', 'DHEA-S', 'Cortisol', 'TSH', 'Estradiol', 'Progesterone'],
      rationale: 'Regular hormone monitoring helps track PCOS management progress and optimize treatment.',
    },
  };
}

export default calculatePCOSAssessment;
